import React, { useState } from 'react';
import { Upload, Card, Typography, Button, Space, theme } from 'antd';
import { FileTextOutlined, CloseCircleOutlined, CheckCircleOutlined } from '@ant-design/icons';

const { Dragger } = Upload;

export interface FileDraggerProps {
  label: string;
  description: string;
  acceptedTypes: string;
  maxSizeMB?: number;
  required?: boolean;
  multiple?: boolean;
  icon?: React.ReactNode;
  iconBgColor?: string;
  iconColor?: string;
  value?: File | File[] | null;
  onChange?: (file: File | File[] | null) => void;
  disabled?: boolean;
}

const FileDragger: React.FC<FileDraggerProps> = ({
  label,
  description,
  acceptedTypes,
  maxSizeMB = 5,
  required = false,
  multiple = false,
  icon,
  iconBgColor,
  iconColor,
  value,
  onChange,
  disabled = false,
}) => {
  const { token } = theme.useToken();
  const [error, setError] = useState<string | null>(null);

  const finalIconBgColor = iconBgColor || token.colorPrimaryBgHover;
  const finalIconColor = iconColor || token.colorPrimary;

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleBeforeUpload = (file: File): boolean => {
    setError(null);

    // Validate file size
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      setError(`El archivo excede el tamaño máximo de ${maxSizeMB}MB`);
      return false;
    }

    // Handle multiple files
    if (multiple) {
      const currentFiles = Array.isArray(value) ? value : value ? [value] : [];
      onChange?.([...currentFiles, file]);
    } else {
      onChange?.(file);
    }

    return false; // Prevent auto-upload
  };

  const handleRemoveFile = (index?: number) => {
    setError(null);
    if (multiple && Array.isArray(value) && index !== undefined) {
      const newFiles = [...value];
      newFiles.splice(index, 1);
      onChange?.(newFiles.length > 0 ? newFiles : null);
    } else {
      onChange?.(null);
    }
  };

  const renderFileList = () => {
    if (!value) return null;

    const files = multiple && Array.isArray(value) ? value : value ? [value] : [];

    return (
      <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {files.map((file, index) => (
          <div
            key={index}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '8px 12px',
              backgroundColor: token.colorFillAlter,
              borderRadius: '8px',
              border: `1px solid ${token.colorBorder}`,
            }}
          >
            <Space>
              <FileTextOutlined style={{ color: token.colorTextSecondary }} />
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <Typography.Text style={{ fontSize: '14px', color: token.colorText }}>
                  {file.name}
                </Typography.Text>
                <Typography.Text style={{ fontSize: '12px', color: token.colorTextSecondary }}>
                  {formatFileSize(file.size)}
                </Typography.Text>
              </div>
            </Space>
            <Button
              type="text"
              icon={<CloseCircleOutlined />}
              onClick={() => handleRemoveFile(index)}
              style={{ color: token.colorTextSecondary }}
              size="small"
            />
          </div>
        ))}
      </div>
    );
  };

  const defaultIcon = (
    <FileTextOutlined style={{ fontSize: '36px' }} />
  );

  return (
    <div style={{ marginBottom: '24px' }}>
      <Dragger
        name="file"
        multiple={multiple}
        maxCount={multiple ? 10 : 1}
        accept={acceptedTypes}
        beforeUpload={handleBeforeUpload}
        showUploadList={false}
        disabled={disabled}
        style={{
          borderRadius: '16px',
          border: error ? `2px dashed ${token.colorError}` : `2px dashed ${token.colorBorder}`,
          background: error ? token.colorErrorBg : token.colorBgLayout,
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '8px 4px',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '12px',
            }}
          >
            <CheckCircleOutlined style={{ color: finalIconColor, fontSize: '20px' }} />
            <Typography.Text
              style={{
                fontSize: '14px',
                fontWeight: 'bold',
                textTransform: 'uppercase',
                letterSpacing: 'wider',
                color: token.colorTextSecondary,
              }}
            >
              {label}
              {required && <span style={{ color: token.colorError }}> *</span>}
            </Typography.Text>
          </div>
          <Card
            style={{
              borderRadius: '16px',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
            }}
            bodyStyle={{ padding: '24px', textAlign: 'center' }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <div
                style={{
                  width: '56px',
                  height: '56px',
                  background: finalIconBgColor,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '16px',
                }}
              >
                {icon || defaultIcon}
              </div>
              <Typography.Title
                level={4}
                style={{ marginBottom: '4px', color: token.colorText, fontSize: '16px' }}
              >
                {label}
              </Typography.Title>
              <Typography.Text
                style={{
                  fontSize: '12px',
                  color: error ? token.colorError : token.colorTextSecondary,
                  lineHeight: '1.5',
                  maxWidth: '260px',
                }}
              >
                {error || description}
              </Typography.Text>
            </div>
          </Card>
        </div>
      </Dragger>
      {renderFileList()}
    </div>
  );
};

export default FileDragger;
